import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { AUDIT_ACTIONS } from "@/lib/constants";
import { formatDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Users, Eye, UserCog, Send, Check, X, LogOut, ArrowRightLeft, History, ShieldCheck, Loader2, Link as LinkIcon } from "lucide-react";
import { cn } from "@/lib/utils";

const ROLE = {
  innsyn: { label: "Innsyn", icon: Eye, description: "Kan se regninger, dokumenter og oversikter. Kan ikke endre noe." },
  handtering: { label: "Håndtering", icon: UserCog, description: "Kan i tillegg laste opp, bekrefte, redigere og markere som betalt. Kan ikke slette eller endre innstillinger." },
};

const StatusPill = ({ status, pendingRegistration = false, awaitingVerification = false }) => {
  const map = { invitert: ["Venter på svar", "status-forfaller_snart"], aktiv: ["Aktiv", "status-betalt"], avslatt: ["Avslått", "status-avvist"], avsluttet: ["Avsluttet", "status-avvist"] };
  const [label, cls, key] = awaitingVerification
    ? ["Venter på e-postbekreftelse", "status-ma_kontrolleres", "venter_bekreftelse"]
    : pendingRegistration
      ? ["Venter på konto", "status-ma_kontrolleres", "venter_konto"]
      : [...(map[status] || [status, "status-avvist"]), status];
  return <span className={cn("inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium", cls)} data-testid={`delegation-status-${key}`}>{label}</span>;
};

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

export default function FamilyPage() {
  const { user, refreshUser, switchActAs } = useAuth();
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState([]);
  const [form, setForm] = useState({ email: "", role: "innsyn", message: "" });
  const [busy, setBusy] = useState("");
  const [endTarget, setEndTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const [d, a] = await Promise.all([api.get("/delegations", { skipActAs: true }), api.get("/delegations/activity", { skipActAs: true, params: { limit: 50 } })]);
      setData(d.data);
      setActivity(a.data.items);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Innlogget bruker åpnet en invitasjonslenke (/familie?invite=<token>): koble invitasjonen til denne kontoen
  const [params, setParams] = useSearchParams();
  const claimed = useRef(false);
  useEffect(() => {
    const token = params.get("invite");
    if (!token || claimed.current) return;
    claimed.current = true;
    api.post("/delegations/claim", { token }, { skipActAs: true })
      .then((r) => {
        const owner = r.data.delegation?.owner?.full_name || "Avsenderen";
        toast.success(r.data.already ? "Invitasjonen er allerede koblet til kontoen din" : `Invitasjonen fra ${owner} er koblet til kontoen din`, { description: "Godta eller avslå den under «Invitasjoner til deg»." });
        load();
        refreshUser();
      })
      .catch((e) => toast.error(errorMessage(e, "Kunne ikke koble invitasjonen")))
      .finally(() => {
        const next = new URLSearchParams(params);
        next.delete("invite");
        setParams(next, { replace: true });
      });
  }, [params, setParams, load, refreshUser]);

  const invite = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) return;
    setBusy("invite");
    try {
      const r = await api.post("/delegations", { email: form.email.trim(), role: form.role, message: form.message || null }, { skipActAs: true });
      const d = r.data;
      if (d.pending_registration) {
        const emailSent = d.invite_email_status === "sendt";
        toast.success(emailSent ? `Invitasjon sendt til ${d.delegate_email}` : `Invitasjonen er registrert på ${d.delegate_email}`, {
          description: emailSent
            ? "Hun får en e-post med lenke til å opprette konto. Fullmakten ligger klar når hun logger inn."
            : "E-post er ikke satt opp, så del invitasjonslenken selv – f.eks. på SMS. Fullmakten ligger klar når kontoen opprettes.",
          duration: 9000,
          action: d.invite_link ? { label: "Kopier lenke", onClick: async () => { (await copyText(d.invite_link)) ? toast.success("Lenke kopiert") : toast.error("Kunne ikke kopiere"); } } : undefined,
        });
      } else {
        toast.success("Invitasjon sendt", { description: `${d.delegate?.full_name || d.delegate_email} har fått invitasjonen og kan godta den under Familie og fullmakt.` });
      }
      setForm({ email: "", role: "innsyn", message: "" });
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy("");
    }
  };

  const copyLink = async (d) => {
    if (!d.invite_link) return;
    (await copyText(d.invite_link)) ? toast.success("Invitasjonslenke kopiert", { description: "Send lenken til personen – kontoen kobles automatisk til invitasjonen." }) : toast.error("Kunne ikke kopiere lenken");
  };
  const resend = (d) => act(`resend-${d.id}`, () => api.post(`/delegations/${d.id}/resend`, null, { skipActAs: true }), `Invitasjonen er sendt på nytt til ${d.delegate_email}`);

  const act = async (key, fn, msg) => {
    setBusy(key);
    try {
      await fn();
      if (msg) toast.success(msg);
      await load();
      await refreshUser();
      window.dispatchEvent(new Event("rp:refresh-notifications"));
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy("");
    }
  };

  const changeRole = (d, role) => act(`role-${d.id}`, () => api.put(`/delegations/${d.id}`, { role }, { skipActAs: true }), "Rolle oppdatert");
  const accept = (d) => act(`accept-${d.id}`, () => api.post(`/delegations/${d.id}/accept`, null, { skipActAs: true }), "Fullmakten er godtatt");
  const decline = (d) => act(`decline-${d.id}`, () => api.post(`/delegations/${d.id}/decline`, null, { skipActAs: true }), "Invitasjonen ble avslått");
  const end = async () => {
    const d = endTarget;
    setEndTarget(null);
    await act(`end-${d.id}`, () => api.delete(`/delegations/${d.id}`, { skipActAs: true }), "Fullmakten er avsluttet");
  };

  if (!data) return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-56" /><Skeleton className="h-56" /></div>;

  const pending = data.received.filter((d) => d.status === "invitert");
  const activeReceived = data.received.filter((d) => d.status === "aktiv");

  return (
    <div className="space-y-6">
      <PageHeader title="Familie og fullmakt" description="Gi ektefelle, barn eller verge tilgang til regningene dine – med tydelige roller, samtykke og full logg." />

      {pending.length ? (
        <Card className="border-primary/40" data-testid="pending-invitations">
          <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Send className="h-4 w-4 text-primary" /> Invitasjoner til deg</CardTitle><CardDescription>Noen ønsker at du skal få tilgang til regningene sine. Du velger selv om du vil godta.</CardDescription></CardHeader>
          <CardContent className="space-y-2">
            {pending.map((d) => (
              <div key={d.id} className="rounded-lg border bg-secondary p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`invitation-${d.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{d.owner?.full_name} <span className="text-muted-foreground font-normal">({d.owner?.email})</span></p>
                  <p className="text-sm text-muted-foreground">Ønsker å gi deg <strong>{ROLE[d.role]?.label.toLowerCase()}</strong>. {ROLE[d.role]?.description}</p>
                  {d.message ? <p className="text-sm mt-1 italic">«{d.message}»</p> : null}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button onClick={() => accept(d)} disabled={!!busy} data-testid={`invitation-accept-${d.id}`}><Check className="h-4 w-4 mr-1" /> Godta</Button>
                  <Button variant="outline" onClick={() => decline(d)} disabled={!!busy} data-testid={`invitation-decline-${d.id}`}><X className="h-4 w-4 mr-1" /> Avslå</Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <div className="space-y-4">
          <Card data-testid="given-delegations">
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4 text-primary" /> Personer med tilgang til mine regninger</CardTitle><CardDescription>Du kan endre rolle eller avslutte når som helst.</CardDescription></CardHeader>
            <CardContent className="space-y-2">
              {data.given.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">Ingen har tilgang til regningene dine.</p> : data.given.map((d) => (
                <div key={d.id} className="rounded-lg border p-3 flex flex-col gap-3" data-testid={`given-${d.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2"><p className="font-medium truncate">{d.delegate?.full_name || d.delegate_email}</p><StatusPill status={d.status} pendingRegistration={d.pending_registration} awaitingVerification={d.awaiting_verification} /></div>
                      <p className="text-xs text-muted-foreground">{d.delegate?.email || d.delegate_email} · {d.status === "aktiv" ? `Godtatt ${formatDateTime(d.accepted_at)}` : `Invitert ${formatDateTime(d.created_at)}`}{d.pending_registration ? ` · ${d.invite_email_status === "sendt" ? "e-post sendt" : "har ikke opprettet konto ennå"}` : ""}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Select value={d.role} onValueChange={(v) => changeRole(d, v)} disabled={!!busy}>
                        <SelectTrigger className="h-9 w-[140px]" aria-label="Rolle" data-testid={`given-role-${d.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-card">{Object.entries(ROLE).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => setEndTarget(d)} disabled={!!busy} data-testid={`given-end-${d.id}`}><X className="h-4 w-4 mr-1" /> {d.status === "aktiv" ? "Avslutt" : "Trekk tilbake"}</Button>
                    </div>
                  </div>
                  {d.pending_registration ? (
                    <div className="rounded-md bg-secondary px-3 py-2 text-xs text-muted-foreground flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`given-pending-${d.id}`}>
                      <span className="flex-1">{d.awaiting_verification
                        ? "Personen har opprettet konto, men har ikke bekreftet e-postadressen ennå. Invitasjonen kobles automatisk når adressen er bekreftet – eller straks hvis hun åpner invitasjonslenken mens hun er innlogget."
                        : "Personen har ikke konto i Controlle ennå. Når kontoen opprettes via lenken – eller e-postadressen bekreftes – ligger invitasjonen klar."}</span>
                      <div className="flex gap-1.5 shrink-0">
                        <Button variant="outline" size="sm" className="h-8" onClick={() => copyLink(d)} data-testid={`given-copy-link-${d.id}`}><LinkIcon className="h-3.5 w-3.5 mr-1" /> Kopier lenke</Button>
                        {data.email_configured ? <Button variant="outline" size="sm" className="h-8" onClick={() => resend(d)} disabled={!!busy} data-testid={`given-resend-${d.id}`}>{busy === `resend-${d.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1" /> Send e-post igjen</>}</Button> : null}
                      </div>
                    </div>
                  ) : null}
                </div>
              ))}
            </CardContent>
          </Card>

          <Card data-testid="invite-card">
            <CardHeader className="pb-3"><CardTitle className="text-base">Inviter en person</CardTitle><CardDescription>Skriv inn e-postadressen til ektefelle, barn eller verge. Har personen ikke konto ennå, får hun en invitasjon med lenke til å opprette en – og fullmakten ligger klar når hun logger inn. Tilgangen starter først når invitasjonen er godtatt.</CardDescription></CardHeader>
            <CardContent>
              <form onSubmit={invite} className="space-y-4" noValidate>
                <div className="grid sm:grid-cols-[1fr_180px] gap-3">
                  <div className="space-y-1.5"><Label htmlFor="inv-email">E-postadresse</Label><Input id="inv-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="navn@eksempel.no" className="h-11" data-testid="invite-email-input" /></div>
                  <div className="space-y-1.5"><Label htmlFor="inv-role">Rolle</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger id="inv-role" className="h-11" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-card">{Object.entries(ROLE).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {Object.entries(ROLE).map(([k, v]) => (
                    <button key={k} type="button" onClick={() => setForm({ ...form, role: k })} className={cn("rounded-lg border p-3 text-left transition-colors hover:bg-secondary", form.role === k && "border-primary bg-accent text-accent-foreground")} data-testid={`invite-role-card-${k}`}>
                      <div className="flex items-center gap-2 font-medium text-sm"><v.icon className="h-4 w-4" /> {v.label}</div>
                      <p className="text-xs text-muted-foreground mt-1">{v.description}</p>
                    </button>
                  ))}
                </div>
                <div className="space-y-1.5"><Label htmlFor="inv-msg">Melding (valgfritt)</Label><Input id="inv-msg" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="f.eks. Kan du hjelpe meg med regningene mine?" data-testid="invite-message-input" /></div>
                <Button type="submit" disabled={busy === "invite" || !form.email.trim()} data-testid="invite-submit-button">{busy === "invite" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />} Send invitasjon</Button>
              </form>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card data-testid="received-delegations">
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><ArrowRightLeft className="h-4 w-4 text-primary" /> Regninger jeg kan se for andre</CardTitle><CardDescription>Bytt til personens regninger, eller avslutt tilgangen.</CardDescription></CardHeader>
            <CardContent className="space-y-2">
              {activeReceived.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">Du har ingen aktive fullmakter fra andre.</p> : activeReceived.map((d) => (
                <div key={d.id} className="rounded-lg border p-3" data-testid={`received-${d.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0"><p className="font-medium truncate">{d.owner?.full_name}</p><p className="text-xs text-muted-foreground">{ROLE[d.role]?.label} · siden {formatDateTime(d.accepted_at)}</p></div>
                    <StatusPill status={d.status} />
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Button size="sm" onClick={() => switchActAs({ owner_id: d.owner_id, owner_name: d.owner?.full_name, role: d.role })} data-testid={`received-open-${d.id}`}><Eye className="h-4 w-4 mr-1" /> Åpne regningene</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEndTarget(d)} disabled={!!busy} data-testid={`received-end-${d.id}`}><LogOut className="h-4 w-4 mr-1" /> Avslutt</Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="bg-secondary">
            <CardContent className="p-4 text-sm text-muted-foreground space-y-2">
              <p className="font-medium text-foreground flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> Slik fungerer fullmakt</p>
              <ul className="list-disc pl-4 space-y-1">
                <li>Tilgang krever samtykke fra den som inviteres.</li>
                <li>Alt som gjøres på dine vegne logges med navn – se loggen under.</li>
                <li>Sletting, eksport, innstillinger og passord er alltid forbeholdt deg.</li>
                <li>Begge parter kan avslutte fullmakten når som helst.</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><History className="h-4 w-4 text-primary" /> Handlinger utført på mine vegne</CardTitle><CardDescription>Hva fullmektiger har gjort i kontoen din.</CardDescription></CardHeader>
        <CardContent>
          {activity.length === 0 ? <p className="text-sm text-muted-foreground">Ingen handlinger ennå.</p> : (
            <ul className="divide-y" data-testid="delegated-activity">
              {activity.map((a) => (
                <li key={a.id} className="py-2 flex items-center justify-between gap-3 text-sm">
                  <span><strong>{a.actor_name}</strong> · {AUDIT_ACTIONS[a.action] || a.action}{a.meta?.fields?.length ? <span className="text-muted-foreground"> ({a.meta.fields.join(", ")})</span> : ""}</span>
                  <span className="text-xs text-muted-foreground tnum shrink-0">{formatDateTime(a.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {data.history.length ? (
        <details className="text-sm">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Tidligere fullmakter ({data.history.length})</summary>
          <ul className="mt-2 divide-y rounded-lg border">
            {data.history.map((d) => (
              <li key={d.id} className="px-3 py-2 flex items-center justify-between gap-2"><span>{d.owner_id === user?.id ? `Til ${d.delegate?.full_name || d.delegate_email}` : `Fra ${d.owner?.full_name}`} · {ROLE[d.role]?.label}</span><span className="flex items-center gap-2"><StatusPill status={d.status} /><span className="text-xs text-muted-foreground tnum">{formatDateTime(d.ended_at || d.updated_at)}</span></span></li>
            ))}
          </ul>
        </details>
      ) : null}

      <AlertDialog open={!!endTarget} onOpenChange={(o) => !o && setEndTarget(null)}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader><AlertDialogTitle>Avslutte fullmakten?</AlertDialogTitle><AlertDialogDescription>{endTarget?.owner_id === user?.id ? `${endTarget?.delegate?.full_name || endTarget?.delegate_email} mister umiddelbart tilgangen til regningene dine.` : `Du mister tilgangen til regningene til ${endTarget?.owner?.full_name}.`} Loggen beholdes.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Avbryt</AlertDialogCancel><AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={end} data-testid="delegation-end-confirm">Avslutt</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
